import { FC, MouseEvent, ReactElement, Ref, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Popover } from '@mantine/core';
import { useLazyPopover } from '../../molecules/PopoverAnchor';
import { usePortalTarget } from '../../provider/UiContext';
import { DTB_POPUP_Z } from '../popupZIndex';
import { HiOutlineMagnifyingGlass, HiXMark } from 'react-icons/hi2';
import { IDataTableChooserOptions } from '../DataTable.types';

export interface IDataTableChooserColumn {
    id: string;
    header: string;
    /** Подпись группы колонки: показывается рядом с именем и участвует в поиске. */
    group?: string;
    visible: boolean;
    /** Нельзя скрывать (allowHiding=false) — рисуем задизейбленной. */
    locked: boolean;
}

interface DataTableColumnChooserProps {
    opened: boolean;
    onClose: () => void;
    /** Кнопка-триггер: получает ref и роли якоря поповера. */
    target: ReactElement<{ ref?: Ref<HTMLElement> }>;
    columns: IDataTableChooserColumn[];
    /** Пакетное применение видимости — один setState на диапазон/«все». */
    onApply: (updates: Record<string, boolean>) => void;
    options?: IDataTableChooserOptions;
}

/** Порядок списка как в кастомном DX-чузере: selectedOnTop поверх sortOrder.
 *  Дефолт (без опций): видимые сверху в порядке колонок таблицы, скрытые ниже
 *  по алфавиту — включённое видно как в таблице, выключенное легко искать. */
function sortChooserColumns(
    columns: IDataTableChooserColumn[],
    options: IDataTableChooserOptions | undefined,
    visibleOf: (c: IDataTableChooserColumn) => boolean,
): IDataTableChooserColumn[] {
    const sortFactor = options?.sortOrder === 'asc' ? 1 : options?.sortOrder === 'desc' ? -1 : 0;
    const selectedOnTop = options?.selectedOnTop ?? true;
    // без явного sortOrder алфавит применяется только к скрытой части списка
    const alphaHiddenOnly = !sortFactor && selectedOnTop;
    if (!sortFactor && !selectedOnTop) return columns;
    const byAlpha = (a: IDataTableChooserColumn, b: IDataTableChooserColumn) =>
        a.header.localeCompare(b.header, 'ru', { sensitivity: 'base' });
    return [...columns].sort((a, b) => {
        if (selectedOnTop) {
            const bySelected = Number(visibleOf(b)) - Number(visibleOf(a));
            if (bySelected) return bySelected;
        }
        if (sortFactor) return sortFactor * byAlpha(a, b);
        if (alphaHiddenOnly && !visibleOf(a) && !visibleOf(b)) return byAlpha(a, b);
        return 0;
    });
}

interface IChooserPanelProps {
    columns: IDataTableChooserColumn[];
    onApply: (updates: Record<string, boolean>) => void;
    onClose: () => void;
    options?: IDataTableChooserOptions;
}

/** Содержимое открытого выбора: ключ — номер открытия, поэтому поиск, черновик и порядок — с открытия. */
const ChooserPanel = ({ columns, onApply, onClose, options }: IChooserPanelProps) => {
    const [search, setSearch] = useState('');
    // Черновик в режиме applyButton: изменения копятся локально до «Выбрать».
    const [draft, setDraft] = useState<Record<string, boolean>>({});
    const anchorIndexRef = useRef<number | null>(null);

    const applyMode = !!options?.applyButton;
    const visibleOf = (c: IDataTableChooserColumn): boolean => (applyMode ? (draft[c.id] ?? c.visible) : c.visible);

    // Порядок фиксируется по состоянию НА ОТКРЫТИИ (selectedOnTop не должен
    // перетасовывать список под курсором при кликах) — но замораживаем только
    // ПОРЯДОК id: сами колонки берём свежие, иначе чекбоксы показывают stale visible.
    const [orderIds] = useState(() => sortChooserColumns(columns, options, (c) => c.visible).map((c) => c.id));
    const ordered = useMemo(() => {
        const byId = new Map(columns.map((c) => [c.id, c]));
        return orderIds.flatMap((id) => {
            const c = byId.get(id);
            return c ? [c] : [];
        });
    }, [columns, orderIds]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q
            ? ordered.filter((c) => c.header.toLowerCase().includes(q) || !!c.group?.toLowerCase().includes(q))
            : ordered;
    }, [ordered, search]);

    const change = (updates: Record<string, boolean>) => {
        if (applyMode) setDraft((prev) => ({ ...prev, ...updates }));
        else onApply(updates);
    };

    const unlocked = filtered.filter((c) => !c.locked);
    const allVisible = unlocked.length > 0 && unlocked.every(visibleOf);
    const someVisible = unlocked.some(visibleOf);

    const onToggleAll = () => {
        const target = !allVisible;
        change(Object.fromEntries(unlocked.map((c) => [c.id, target])));
    };

    const onCheckClick = (index: number, c: IDataTableChooserColumn) => (e: MouseEvent<HTMLInputElement>) => {
        const anchor = anchorIndexRef.current;
        anchorIndexRef.current = index;
        if (!e.shiftKey || anchor == null || anchor === index) return;
        e.preventDefault(); // диапазон ставим сами (состояние = целевое кликнутого)
        const target = !visibleOf(c);
        const [from, to] = anchor < index ? [anchor, index] : [index, anchor];
        const updates: Record<string, boolean> = {};
        for (let i = from; i <= to; i++) {
            const item = filtered[i];
            if (item && !item.locked) updates[item.id] = target;
        }
        change(updates);
    };

    const apply = () => {
        onApply(draft);
        onClose();
    };

    return (
        <>
            <div className="dtb-menu__head">
                <div className="dtb-menu__label">Выбор столбцов</div>
                <button type="button" className="dtb-menu__close" aria-label="Закрыть выбор столбцов" onClick={onClose}>
                    <HiXMark aria-hidden />
                </button>
            </div>
            <div className="dtb-hfsearch">
                <HiOutlineMagnifyingGlass aria-hidden />
                <input
                    value={search}
                    placeholder="Поиск"
                    onChange={(e) => {
                        // Якорь — индекс в отфильтрованном списке: смена поиска делает его чужим,
                        // и диапазон тогглил бы не те колонки, что были на экране.
                        anchorIndexRef.current = null;
                        setSearch(e.target.value);
                    }}
                    aria-label="Поиск столбцов"
                />
            </div>
            <label className="dtb-menu__check dtb-hfall">
                <input
                    type="checkbox"
                    checked={allVisible}
                    ref={(el) => {
                        if (el) el.indeterminate = someVisible && !allVisible;
                    }}
                    onChange={onToggleAll}
                    aria-label="Выделить все столбцы"
                />
                <span className="dtb-hfvalue">(Выделить все)</span>
            </label>
            <div className="dtb-menu__scroll">
                {filtered.map((c, i) => (
                    <label key={c.id} className={clsx('dtb-menu__check', c.locked && 'is-locked')}>
                        <input
                            type="checkbox"
                            checked={visibleOf(c)}
                            disabled={c.locked}
                            onClick={onCheckClick(i, c)}
                            onChange={() => change({ [c.id]: !visibleOf(c) })}
                        />
                        <span className="dtb-hfvalue" title={c.group ? `${c.group}: ${c.header}` : c.header}>
                            {c.header}
                        </span>
                        {c.group && <span className="dtb-chooser__group">{c.group}</span>}
                    </label>
                ))}
                {!filtered.length && <div className="dtb-hfempty">Нет столбцов</div>}
            </div>
            {applyMode && (
                <div className="dtb-chooser__foot">
                    <button type="button" className="dtb-chooser__btn dtb-chooser__btn--primary" onClick={apply}>
                        Выбрать
                    </button>
                    <button type="button" className="dtb-chooser__btn" onClick={onClose}>
                        Закрыть
                    </button>
                </div>
            )}
        </>
    );
};

/** Выбор колонок — паритет кастомного DX ColumnChooser: поиск, «(Выделить все)»,
 *  Shift-диапазон, sortOrder/selectedOnTop, режим applyButton («Выбрать»/«Закрыть»).
 *  Popover — с первого открытия: до него рисуется только кнопка, список колонок не перебирается. */
const DataTableColumnChooser: FC<DataTableColumnChooserProps> = ({
    opened,
    onClose,
    target,
    columns,
    onApply,
    options,
}) => {
    const portalTarget = usePortalTarget();
    const popover = useLazyPopover({ opened, haspopup: 'dialog', target });
    // Номер открытия — ключ панели: открытие во время перехода закрытия застаёт её на экране, а черновик — с открытия
    const [openings, setOpenings] = useState({ opened, count: 0 });
    if (openings.opened !== opened) setOpenings({ opened, count: openings.count + (opened ? 1 : 0) });

    return (
        <>
            {popover.anchor}
            {popover.mounted && (
                <Popover
                    {...popover.popoverProps}
                    onChange={(o) => {
                        if (!o) onClose();
                    }}
                    position="bottom-end"
                    offset={6}
                    zIndex={DTB_POPUP_Z}
                    portalProps={portalTarget ? { target: portalTarget } : undefined}
                    // фиксированная ширина ПРОПОМ: Mantine ставит inline width: max-content,
                    // css-класс его не перебивает — дропдаун «переезжал» при вводе в поиск
                    width={300}
                    classNames={{ dropdown: 'dtb-menu dtb-chooser' }}
                >
                    {popover.popoverTarget}
                    <Popover.Dropdown>
                        <ChooserPanel
                            key={openings.count}
                            columns={columns}
                            onApply={onApply}
                            onClose={onClose}
                            options={options}
                        />
                    </Popover.Dropdown>
                </Popover>
            )}
        </>
    );
};

export default DataTableColumnChooser;
